import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Spinner, Dropdown } from "react-bootstrap";
import { Checkbox } from "antd";
import { FilterOutlined, AlibabaOutlined as Download, CaretLeftFilled as Printer } from "@ant-design/icons";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import exportService from "../utils/exportService"; // Adjust path as needed

// TypeScript-like prop interface for documentation
/* 
interface ReportPreviewProps {
  visible: boolean;
  onClose: () => void;
  reportTitle: string;
  propertyName?: string;
  data: Array<Record<string, any>>;
  headers: Array<{ key: string; title: string }>;
  footerData?: Array<{ key: string; value: any }>;
  totalColumns?: Array<string>; // Columns to calculate totals for
  onDownloadPDF?: () => void;
  apiEndpoint?: string;
  apiUrl?: string;
}
*/

const PrintReportPreview = ({
  visible,
  onClose,
  reportTitle,
  propertyName = "Your Property Name",
  data,
  headers,
  footerData = [],
  totalColumns = [], // Default to empty array if not provided
  onDownloadPDF,
  apiEndpoint = "transactions/generate-pdf",
  apiUrl = process.env.REACT_APP_API_URL,
}) => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isSwitchingView, setIsSwitchingView] = useState(false); // Loader for view switches
  const [companyData, setCompanyData] = useState({
    company: "Company Name",
    address: "Address",
    city: "City",
    phone: "N/A",
    email: "N/A",
  });
  const componentRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [outputType, setOutputType] = useState("html"); // Toggle between html and pdf

  const cdnUrl = process.env.REACT_APP_CDN_URL;

    const companyLogo = localStorage.getItem("companylogo");

  const logoNetShine = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : 'https://netshine-assets.blr1.digitaloceanspaces.com/company_87/logos/company_logo_87_1739613280498';
  const defaultLogoSrc = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : logoNetShine;


  // Fetch and cache company details
  useEffect(() => {
    let isMounted = true;
    const fetchCompanyDetails = async () => {
      try {
        const cachedData = localStorage.getItem("companyDetails");
        if (cachedData) {
          setCompanyData(JSON.parse(cachedData));
          return;
        }

        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiUrl}/company/company-details/001`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.data) {
          const data = {
            company: response.data.company || "Company Name",
            address: response.data.address || "Address",
            city: response.data.city || "City",
            phone: response.data.phone || "N/A",
            email: response.data.email || "N/A",
          };
          if (isMounted) {
            setCompanyData(data);
            localStorage.setItem("companyDetails", JSON.stringify(data));
          }
        } else {
          console.warn("Failed to fetch company details: API success is false.");
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
        if (isMounted) {
          setCompanyData({
            company: "Company Name",
            address: "Address",
            city: "City",
            phone: "N/A",
            email: "N/A",
          });
        }
      }
    };

    if (visible) fetchCompanyDetails();
    return () => { isMounted = false; };
  }, [visible, apiUrl]);

  useEffect(() => {
    if (visible) {
      console.log("Modal opened, data:", data, "headers:", headers); // Debug log
      setSelectedColumns(headers.length > 0 ? headers.map((header) => header.key) : []);
      setPdfUrl(""); // Reset PDF URL on open
    }
  }, [visible, headers, data]);

  const handleColumnToggle = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((col) => col !== key) : [...prev, key]
    );
  };

  const handlePrint = () => {
    const printContent = componentRef.current?.outerHTML || "";
    if (!printContent) {
      Swal.fire({
        title: "Print Error",
        text: "No content to print.",
        icon: "error",
      });
      return;
    }
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print ${reportTitle}</title>
          <style>
            @page { size: A4; margin: 10mm; }
            body { font-family: Arial, sans-serif; font-size: 12px; color: #333; margin: 0; padding: 0; background-color: #fff; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .top-bar { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; border-bottom: 1px solid #dee2e6; padding-bottom: 10px; }
            .property-info { text-align: right; font-size: 11px; line-height: 1.5; }
            .footer { text-align: right; margin-top: 20px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handlePdfView = async () => {
    setIsLoadingPdf(true);
    const htmlContent = componentRef.current?.outerHTML || "";
    if (!htmlContent) {
      Swal.fire({
        title: "Error",
        text: "No content to generate PDF.",
        icon: "error",
      });
      setIsLoadingPdf(false);
      return;
    }

    let isMounted = true;
    try {
      console.log("Sending PDF request:", htmlContent); // Debug API call
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${apiUrl}/${apiEndpoint}`,
        { html: htmlContent },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "arraybuffer",
        }
      );

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      if (isMounted) {
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
        setOutputType("pdf"); // Switch to PDF view
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      if (isMounted) {
        Swal.fire({
          title: "Error",
          text: err.message || "Failed to generate PDF from server.",
          icon: "error",
        });
      }
    } finally {
      if (isMounted) setIsLoadingPdf(false);
    }
  };

  const handleViewSwitch = async () => {
    setIsSwitchingView(true);
    try {
      if (outputType === "html") {
        // Switching to PDF, so generate it first
        await handlePdfView();
      } else {
        // Releasing the blob URL if switching back
        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
          setPdfUrl("");
        }
        setOutputType("html");
      }
    } catch (err) {
      console.error("Error switching view:", err);
      Swal.fire({
        title: "Error",
        text: "Failed to switch view.",
        icon: "error",
      });
    } finally {
      setIsSwitchingView(false);
    }
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `${reportTitle.toLowerCase().replace(/\s/g, "_")}_${moment().format("YYYYMMDD")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExportXLS = () => {
    exportService.exportToXLS(
      data,
      headers.map((header) => ({ key: header.key, label: header.title })),
      `${reportTitle.toLowerCase().replace(/\s/g, "_")}_${moment().format("YYYYMMDD")}.xlsx`,
      reportTitle
    );
  };

  const handleExportCSV = () => {
    exportService.exportToCSV(
      data,
      headers.map((header) => ({ key: header.key, label: header.title })),
      `${reportTitle.toLowerCase().replace(/\s/g, "_")}_${moment().format("YYYYMMDD")}.csv`
    );
  };

  const downloadMenu = (
    <Dropdown.Menu>
      <Dropdown.Item onClick={handleExportXLS}>Export XLS</Dropdown.Item>
      <Dropdown.Item onClick={handleExportCSV}>Export CSV</Dropdown.Item>
    </Dropdown.Menu>
  );

  const renderReportContent = () => (
    <div
      className="report-container"
      ref={componentRef}
      style={{
        padding: "10mm",
        fontFamily: "Arial, sans-serif",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "auto",
      }}
    >
<div
  className="top-bar"
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center", // Align items vertically in the center
    marginBottom: "10px",
    borderBottom: "1px solid #dee2e6",
    paddingBottom: "10px",
    width: "100%",
  }}
>
  {/* Left: Logo */}
  <div className="logo" style={{ flex: "1", textAlign: "left" }}>
    <img
      src={defaultLogoSrc || "https://via.placeholder.com/100"}
      alt="Company Logo"
      style={{ maxWidth: "280px", height: "auto", maxHeight: "80px" }}
    />
  </div>

  {/* Center: Title */}
  <div
    className="report-title"
    style={{
      flex: "1",
      textAlign: "center",
      fontSize: "16px",
      fontWeight: "bold",
      color: "#333",
    }}
  >
    <h1 style={{ margin: 0, fontSize: "30px" }}>{reportTitle}</h1>
  </div>

  {/* Right: Property Info */}
  <div
    className="property-info"
    style={{
      flex: "1",
      textAlign: "right",
      fontSize: "11px",
      lineHeight: "1.5",
    }}
  >
    <h1 style={{ fontSize: "14px", margin: 0 }}>{companyData.company}</h1>
    <p style={{ margin: "2px 0", fontSize: "8px" }}>
      {companyData.address}, {companyData.city}
    </p>
    <p style={{ margin: "2px 0", fontSize: "8px" }}>
      Phone: {companyData.phone} | Email: {companyData.email}
    </p>
  </div>
</div>
      <div style={{ marginBottom: "15px", width: "100%" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr style={{ backgroundColor: "#f2f2f2" }}>
              {headers
                .filter((header) => selectedColumns.length === 0 || selectedColumns.includes(header.key))
                .map((header) => (
                  <th
                    key={header.key}
                    style={{
                      border: "1px solid #ddd",
                      padding: "8px",
                      textAlign: "left",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header.title || header.label}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((item, index) => (
                <tr key={index} style={{ borderBottom: "1px solid #ddd", backgroundColor: "#fff" }}>
                  {headers
                    .filter((header) => selectedColumns.length === 0 || selectedColumns.includes(header.key))
                    .map((header) => (
                      <td
                        key={header.key}
                        style={{
                          border: "1px solid #ddd",
                          padding: "8px",
                          textAlign: "left",
                          backgroundColor: "#fff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item[header.key] !== undefined ? item[header.key] : "N/A"}
                      </td>
                    ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={headers.length}
                  style={{ textAlign: "center", padding: "20px", color: "#666", backgroundColor: "#fff" }}
                >
                  No data available.
                </td>
              </tr>
            )}
            {totalColumns.length > 0 && (
              <tr style={{ borderBottom: "1px solid #ddd", backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                <td
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    fontWeight: totalColumns.includes(headers[0].key) ? "normal" : "bold",
                  }}
                >
                  {!totalColumns.includes(headers[0].key) && "Total:"}
                </td>
                {headers
                  .filter((header) => selectedColumns.length === 0 || selectedColumns.includes(header.key))
                  .slice(4) // Skip the first 4 columns for total alignment
                  .map((header) => (
                    <td
                      key={header.key}
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "left",
                        backgroundColor: "#e6f4ea",
                        fontWeight: totalColumns.includes(header.key) ? "bold" : "normal",
                      }}
                    >
                      {totalColumns.includes(header.key) ? calculateTotal(header.key).toFixed(2) : ""}
                    </td>
                  ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {footerData.length > 0 && (
        <div
          className="footer"
          style={{
            textAlign: "right",
            marginTop: "20px",
            fontSize: "12px",
            color: "#666",
            width: "100%", // Match table width
          }}
        >
          {footerData.map((footer) => (
            <p key={footer.key} style={{ margin: "5px 0" }}>
              <strong>{footer.key}:</strong> {footer.value}
            </p>
          ))}
        </div>
      )}
    </div>
  );

  const columnSelectionContent = (
    <div
      style={{
        padding: "12px",
        maxHeight: "400px",
        width: "400px",
        overflowY: "auto",
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: "6px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "12px",
        }}
      >
        <Checkbox
          checked={selectedColumns.length === headers.length}
          indeterminate={
            selectedColumns.length > 0 && selectedColumns.length < headers.length
          }
          onChange={(e) =>
            setSelectedColumns(e.target.checked ? headers.map((h) => h.key) : [])
          }
        />
        <strong>Select All</strong>
      </div>
      {headers.length > 0 ? (
        headers.map((header) => (
          <div
            key={header.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "4px 0",
            }}
          >
            <Checkbox
              checked={selectedColumns.includes(header.key)}
              onChange={() => handleColumnToggle(header.key)}
            />
            <span>{header.title || header.label}</span>
          </div>
        ))
      ) : (
        <p style={{ color: "#666" }}>No columns available.</p>
      )}
    </div>
  );

  const handleDropdownToggle = (isOpen) => {
    setDropdownOpen(isOpen);
  };

  const renderContent = () => {
    if (outputType === "pdf" && pdfUrl) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <iframe
            src={pdfUrl}
            style={{
              width: "100%",
              maxWidth: "100%",
              height: "100%",
              border: "none",
            }}
            title={`${reportTitle} PDF`}
            loading="lazy"
            scrolling="auto"
          />
        </div>
      );
    }
    return data && data.length > 0 ? (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          minHeight: "100%",
        }}
      >
        <div
          style={{
            width: "auto",
            maxWidth: "100%",
            height: "auto",
            padding: "10px",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
          }}
        >
          {renderReportContent()}
        </div>
      </div>
    ) : (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <p>No data available.</p>
      </div>
    );
  };

  // Calculate totals for specified columns
  const calculateTotal = (columnKey) => {
    return data.reduce((sum, item) => {
      const value = item[columnKey];
      if (typeof value === "string" && value.includes("/")) {
        const parts = value.split("/").map(Number);
        return sum + (parts.reduce((a, b) => a + b, 0) || 0);
      }
      return sum + (Number(value) || 0);
    }, 0);
  };

  return (
    <Modal
      show={visible}
      onHide={onClose}
      keyboard={false}
      dialogClassName="folio-preview-modal-dialog"
      backdropClassName="modal-backdrop"
      style={{ zIndex: 1060 }}
    >
      <Modal.Header
        style={{
          flexShrink: 0,
          padding: "10px 20px",
          borderBottom: "1px solid #dee2e6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
        }}
      >
        <Modal.Title id="reportPreviewModalLabel">{reportTitle}</Modal.Title>
        <Dropdown show={dropdownOpen} onToggle={handleDropdownToggle}>
          <Dropdown.Toggle
            variant="outline-primary"
            id="dropdown-select-columns"
            style={{ fontSize: "14px", padding: "5px 10px" }}
          >
            <FilterOutlined /> Select Columns
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ maxHeight: "400px", overflowY: "auto", width: "400px" }}>
            {columnSelectionContent}
          </Dropdown.Menu>
        </Dropdown>
      </Modal.Header>
      <Modal.Body
        style={{
          flex: 1,
          padding: "5mm",
          overflowY: "auto",
          height: "calc(100vh - 140px)",
          position: "relative",
        }}
      >
        {(isLoadingPdf || isSwitchingView) ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <Spinner animation="border" role="status" style={{ width: "3rem", height: "3rem" }} />
            <p style={{ marginTop: "10px", fontSize: "16px" }}>
              {isLoadingPdf ? "Generating PDF..." : "Switching View..."}
            </p>
          </div>
        ) : (
          renderContent()
        )}
      </Modal.Body>
      <Modal.Footer
        style={{
          flexShrink: 0,
          padding: "10px 20px",
          borderTop: "1px solid #dee2e6",
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "10px",
          width: "100%", // Match table width
        }}
      >
        <Button
          variant={outputType === "pdf" ? "outline-primary" : "primary"}
          onClick={handleViewSwitch}
          disabled={isLoadingPdf || !data.length}
          style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}
        >
          Switch to {outputType === "pdf" ? "HTML" : "PDF"} View
        </Button>
        {outputType === "pdf" ? (
          <Button
            variant="primary"
            onClick={handleDownloadPDF}
            disabled={!pdfUrl || isLoadingPdf}
            style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}
          >
            <Download size={16} className="me-2" /> Download
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handlePrint}
            disabled={isLoadingPdf || !data.length}
            style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}
          >
            <Printer size={16} className="me-2" /> Print
          </Button>
        )}
        <Dropdown>
          <Dropdown.Toggle
            variant="secondary"
            id="dropdown-download"
            style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}
          >
            <Download size={16} className="me-2" /> Download
          </Dropdown.Toggle>
          {downloadMenu}
        </Dropdown>
        <Button
          variant="secondary"
          onClick={onClose}
          style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}
        >
          Close
        </Button>
      </Modal.Footer>
      <style jsx>{`
        .print-view {
          display: block;
        }
        @media print {
          .modal,
          .modal-content,
          .modal-header,
          .modal-footer {
            display: none !important;
          }
          .print-view {
            display: block !important;
            width: 100%;
            box-sizing: border-box;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          th,
          td {
            border: 1px solid #000;
            padding: 8px;
            font-size: 10pt;
            background-color: #fff;
          }
          th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
        }
        .folio-preview-modal-dialog {
          width: 100%;
          max-width: 100%;
          position: fixed;
          right: 0;
          top: 0;
          height: 100vh;
          margin: 0;
          zIndex: 1060;
          transform: translateX(100%);
          transition: transform 0.3s ease-in-out;
        }
        .folio-preview-modal-dialog.show {
          transform: translateX(0);
        }
      `}</style>
    </Modal>
  );
};

export default PrintReportPreview;
